import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, Select, EmptyState } from "@/components/ui";
import { toast } from "sonner";
import { ListChecks } from "lucide-react";

const STATUSES = ["Open", "In Progress", "Completed"];

export default function WorkQueue() {
  const [tasks, setTasks] = useState(null);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");

  const load = () => api.get("/tasks", { params: { status, priority } }).then((r) => setTasks(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, priority]);

  const updateStatus = async (id, newStatus) => {
    await api.patch(`/tasks/${id}`, { status: newStatus });
    toast.success(`Task marked ${newStatus}`);
    load();
  };

  return (
    <div className="vc-fade-up">
      <PageHeader title="Work Queue" subtitle="Automated triggers, care alerts, and coordination tasks routed to your team." />

      <Card className="p-4 mb-4 flex flex-wrap gap-3">
        <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="wq-filter-status">
          <option value="">All Statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
        </Select>
        <Select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="wq-filter-priority">
          <option value="">All Priorities</option><option>Urgent</option><option>High</option><option>Medium</option><option>Low</option>
        </Select>
      </Card>

      {tasks === null ? <Spinner /> : tasks.length === 0 ? <EmptyState title="No tasks" /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Task</th><th className="px-4 py-3 font-semibold">Member</th>
                <th className="px-4 py-3 font-semibold">Type</th><th className="px-4 py-3 font-semibold">Priority</th>
                <th className="px-4 py-3 font-semibold">Due</th><th className="px-4 py-3 font-semibold">Assigned</th>
                <th className="px-4 py-3 font-semibold">Status</th></tr></thead>
              <tbody className="divide-y divide-border">
                {tasks.map((t) => (
                  <tr key={t.id} className="hover:bg-muted transition-colors" data-testid={`task-row`}>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-brand-700 shrink-0" /><span className="font-medium">{t.title}</span></div></td>
                    <td className="px-4 py-3 text-muted-foreground">{t.member_name}</td>
                    <td className="px-4 py-3"><span className="text-xs rounded bg-slate-100 px-2 py-0.5 text-muted-foreground">{t.type}</span></td>
                    <td className="px-4 py-3"><Badge tone={t.priority}>{t.priority}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground">{t.due_date}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.assigned_to}</td>
                    <td className="px-4 py-3">
                      <select value={t.status} onChange={(e) => updateStatus(t.id, e.target.value)} data-testid={`task-status-${t.id}`}
                        className="h-8 rounded border border-input bg-card px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring">
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
